import React, { useState } from "react";
import { Share2, FileText, Image as ImageIcon, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useLanguage } from "../context/LanguageContext";
import { shareProfileOnWhatsApp } from "../helpers/profileExport.helper";

type ShareModalProps = {
  profile: any;
  setIsShareModalOpen: (open: boolean) => void;
};

const ShareModal = ({ profile, setIsShareModalOpen }: ShareModalProps) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<"main" | "whatsapp" | "biodata">("main");

  const handleShareProfile = async (includePublicLink: boolean) => {
    try {
      await shareProfileOnWhatsApp(profile, { includePublicLink });
      toast.success("Profile opened in WhatsApp share.");
      setIsShareModalOpen(false);
    } catch (error) {
      console.error("Share failed:", error);
      toast.error("Unable to share profile on WhatsApp.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            {step !== "main" && (
              <button
                onClick={() => setStep("main")}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="text-xl font-bold">
              {step === "main"
                ? "Share Options"
                : step === "whatsapp"
                  ? "Share on WhatsApp"
                  : "Share Biodata"}
            </h2>
          </div>
          <button
            onClick={() => setIsShareModalOpen(false)}
            className="text-2xl font-bold hover:text-gray-600"
          >
            ×
          </button>
        </div>

        {step === "main" ? (
          <div className="space-y-3">
            {/* Share Profile URL */}
            <button
              onClick={() => setStep("whatsapp")}
              className="w-full flex items-center gap-4 border border-gray-300 p-4 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="p-2 bg-purple-100 rounded-lg">
                <Share2 size={24} className="text-purple-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-gray-900">Share on WhatsApp</p>
                <p className="text-sm text-gray-500">
                  Share biodata text and up to 5 images
                </p>
              </div>
            </button>

            {/* Share Biodata */}
            <button
              onClick={() => setStep("biodata")}
              className="w-full flex items-center gap-4 border border-gray-300 p-4 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText size={24} className="text-amber-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-gray-900">Share Biodata</p>
                <p className="text-sm text-gray-500">
                  Share biodata as PDF or Image
                </p>
              </div>
            </button>
          </div>
        ) : step === "whatsapp" ? (
          <div className="space-y-3">
            <button
              onClick={() => handleShareProfile(true)}
              className="w-full flex items-center gap-4 border border-gray-300 p-4 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <Share2 size={24} className="text-green-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-gray-900">With Public Link</p>
                <p className="text-sm text-gray-500">
                  Share biodata text, public link, main image and up to 4 gallery images
                </p>
              </div>
            </button>

            <button
              onClick={() => handleShareProfile(false)}
              className="w-full flex items-center gap-4 border border-gray-300 p-4 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <Share2 size={24} className="text-blue-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-gray-900">Without Public Link</p>
                <p className="text-sm text-gray-500">
                  Share biodata text, main image and up to 4 gallery images without the link
                </p>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Share Biodata as PDF */}
            <button
              onClick={() => {
                setIsShareModalOpen(false);
                // Trigger PDF share - you'll handle this in parent
                const event = new CustomEvent("shareBiodata", {
                  detail: { type: "pdf" },
                });
                window.dispatchEvent(event);
              }}
              className="w-full flex items-center gap-4 border border-gray-300 p-4 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="p-2 bg-red-100 rounded-lg">
                <FileText size={24} className="text-red-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-gray-900">Share as PDF</p>
                <p className="text-sm text-gray-500">
                  Choose a frame & share PDF biodata
                </p>
              </div>
            </button>

            {/* Share Biodata as Image */}
            <button
              onClick={() => {
                setIsShareModalOpen(false);
                // Trigger Image share - you'll handle this in parent
                const event = new CustomEvent("shareBiodata", {
                  detail: { type: "image" },
                });
                window.dispatchEvent(event);
              }}
              className="w-full flex items-center gap-4 border border-gray-300 p-4 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <ImageIcon size={24} className="text-green-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-gray-900">Share as Image</p>
                <p className="text-sm text-gray-500">
                  Choose a frame & share image biodata
                </p>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
